/**
 * The shared frame for every signed-out screen — log in, sign up, forgotten
 * password, reset, verify email, accept an invite, unsubscribe.
 *
 * The look is direction D on the design canvas (docs/DECISIONS.md, 10 October
 * 2026): a deep green band around a stone page, a green curve rising from the
 * bottom, the form on a pale card, and one two-tone sentence beside it.
 *
 * Extracted so the frame exists once. Seven copies of a background is how
 * design drift starts.
 */
import { useState, type ReactNode } from 'react';
import { c, display, field, label, sans } from '../theme.js';
import { PasswordInput } from '../app/PasswordInput.js';
import { HeroCard } from './ExampleCard.js';

/**
 * The green curve behind the landing hero and the sign-in pages. Three stacked
 * ellipses: the field itself, a hairline highlight, and a soft streak of light.
 * Decoration only, so it is hidden from assistive tech and never takes clicks.
 */
export function GreenSweep({ lightAt = 62 }: { lightAt?: number }) {
  const layer: React.CSSProperties = {
    position: 'absolute',
    borderRadius: '50%',
    pointerEvents: 'none',
  };
  return (
    <div
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}
    >
      <div
        style={{
          ...layer,
          // Much wider than the page, so the visible top edge is a shallow arc
          // and text set near the bottom corners is still over green.
          left: '-32%',
          width: '164%',
          bottom: '-60%',
          height: '104%',
          background: `radial-gradient(60% 58% at ${lightAt}% 8%, #3F6B52 0%, #24402F 34%, #16241C 72%)`,
        }}
      />
      <div
        style={{
          ...layer,
          left: '-24%',
          width: '140%',
          bottom: '-57%',
          height: '95%',
          borderTop: '1px solid rgba(232,226,208,0.35)',
          transform: 'rotate(-4deg)',
        }}
      />
      <div
        style={{
          ...layer,
          left: '-18%',
          width: '136%',
          bottom: '-58%',
          height: '95%',
          background: `radial-gradient(36% 18% at ${lightAt - 4}% 2%, rgba(226,214,180,0.34) 0%, rgba(226,214,180,0) 70%)`,
        }}
      />
    </div>
  );
}

/** The small logo pill. A button when it leads somewhere, plain text when not. */
export function Wordmark({
  onClick,
  onDark = false,
}: {
  onClick?: (() => void) | undefined;
  onDark?: boolean;
}) {
  const inner = (
    <>
      <span
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          borderRadius: 7,
          background: onDark ? c.appBg : c.ink,
          color: onDark ? c.ink : c.appBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1,
          paddingBottom: 2,
        }}
      >
        g
      </span>
      <span
        style={{ fontSize: 16, fontWeight: 500, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}
      >
        the good stock.
      </span>
    </>
  );
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 9,
    background: onDark ? 'transparent' : '#EDEBE4',
    color: onDark ? c.white : c.ink,
    padding: onDark ? 0 : '6px 12px 6px 8px',
    borderRadius: 18,
    border: 'none',
    fontFamily: sans,
    minHeight: onDark ? undefined : 44,
  };
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-label="The Good Stock home"
      style={{ ...style, cursor: 'pointer' }}
    >
      {inner}
    </button>
  ) : (
    <div style={style}>{inner}</div>
  );
}

export function AuthLayout({
  heading,
  intro,
  children,
  onHome,
  aside,
  centered = false,
}: {
  heading: string;
  intro?: string;
  children: ReactNode;
  /** Makes the logo a link back to the landing page. */
  onHome?: (() => void) | undefined;
  /** The sentence in the bottom-left corner. Each screen may say its own thing. */
  aside?: { lead: string; rest: string } | undefined;
  /**
   * A wider card, for long forms. The name is historical: every screen is
   * centred now, and this only chooses the width. Sign-up has six fields and a
   * plan picker; log-in has two.
   */
  centered?: boolean;
}) {
  const line = aside ?? {
    lead: 'Your shelves are where you left them.',
    rest: 'Log in to see what moved, what is running low and what to use first.',
  };
  const wide = centered;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.frame,
        padding: 12,
        fontFamily: sans,
        color: c.ink,
        display: 'flex',
      }}
    >
      {/*
        The form sits in the middle on every signed-out screen. The two bottom
        corners, over the green, carry what would otherwise be dead space: the
        sentence on the left, an example of the product on the right. They
        arrive a beat after the card, and are dropped — not squeezed — when the
        window is too narrow to hold them beside it.
      */}
      <style>{`
        .gs-side { display: none; }
        @media (min-width: 1100px) { .gs-side.gs-for-narrow { display: block; } }
        @media (min-width: 1280px) { .gs-side.gs-for-wide { display: block; } }
        @keyframes gs-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .gs-rise { animation: gs-rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .gs-rise-2 { animation-delay: 0.14s; }
        .gs-rise-3 { animation-delay: 0.26s; }
        @media (prefers-reduced-motion: reduce) { .gs-rise { animation: none; } }
        @media (max-width: 640px) { .gs-auth-card { padding: 78px 4px 20px !important; } }
      `}</style>
      <div
        style={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          background: `radial-gradient(56% 48% at 50% 26%, #F1EFE8 0%, rgba(241,239,232,0) 72%), ${c.authBg}`,
        }}
      >
        <GreenSweep lightAt={50} />

        <div style={{ position: 'absolute', left: 16, top: 14, zIndex: 2 }}>
          <Wordmark onClick={onHome} />
        </div>

        <div
          className={`gs-side gs-rise gs-rise-2 ${wide ? 'gs-for-wide' : 'gs-for-narrow'}`}
          style={{ position: 'absolute', left: 28, bottom: 30, width: wide ? 248 : 300 }}
        >
          <div style={{ fontSize: 13, fontWeight: 500, color: '#B9C4BB', marginBottom: 10 }}>
            Today
          </div>
          <p
            style={{
              ...display,
              margin: 0,
              fontSize: wide ? 23 : 27,
              lineHeight: 1.14,
              letterSpacing: '-0.03em',
              color: '#F1EFE8',
            }}
          >
            {line.lead} <span style={{ color: '#B4BEB5' }}>{line.rest}</span>
          </p>
        </div>

        <div
          className={`gs-side gs-rise gs-rise-3 ${wide ? 'gs-for-wide' : 'gs-for-narrow'}`}
          aria-label="Example figures from a sample clinic"
          style={{
            position: 'absolute',
            right: 28,
            bottom: 30,
            width: wide ? 240 : 262,
            transform: 'rotate(1.2deg)',
            transformOrigin: 'bottom right',
          }}
        >
          <HeroCard
            title="Going out of date"
            sub="in the next 12 weeks"
            figure="£ 705.50"
            tag={{ text: '1 expired', tone: 'danger' }}
            tagNote="across 4 batches"
            rows={[
              ['Lidocaine 5%', 'expired', true],
              ['Azzalure 125u', '18 days'],
              ['Heliocare 360', '41 days'],
            ]}
          />
          {/* Under the card, not over it: the card's top edge can sit above the
              green on a short window, where pale text would vanish. */}
          <div
            style={{
              fontSize: 12.5,
              color: '#B9C4BB',
              margin: '10px 2px 0',
              textAlign: 'right',
            }}
          >
            Example clinic
          </div>
        </div>

        <div
          className="gs-auth-card gs-rise"
          style={{
            position: 'relative',
            width: wide ? 700 : 500,
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: wide ? '68px 12px 32px' : '84px 12px 44px',
          }}
        >
          <div
            style={{
              width: '100%',
              background: c.card,
              borderRadius: 18,
              padding: wide ? '28px 36px 24px' : '34px 36px 30px',
              boxShadow: '0 40px 70px -34px rgba(0,0,0,0.6)',
            }}
          >
            <h1 style={{ ...display, fontSize: 40, fontWeight: 300, margin: '0 0 6px', color: c.ink }}>{heading}</h1>
            {intro && (
              <p style={{ fontSize: 15.5, color: c.muted, margin: '0 0 24px', lineHeight: 1.5 }}>
                {intro}
              </p>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  labelText: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  hint?: string | undefined;
  autoFocus?: boolean;
}

export function Field({
  id,
  labelText,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  required,
  hint,
  autoFocus,
}: FieldProps) {
  const [focused, setFocused] = useState(false);
  // A password gets the box with a show/hide button; everything else is plain.
  const Box = type === 'password' ? PasswordInput : 'input';
  return (
    <div>
      <label htmlFor={id} style={label}>
        {labelText}
      </label>
      <Box
        id={id}
        {...(type === 'password' ? {} : { type })}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...field, borderColor: focused ? c.green : c.borderStrong }}
      />
      {hint && <p style={{ fontSize: 13, color: c.faint, margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: c.dangerBg,
        border: `1px solid ${c.dangerBorder}`,
        color: c.danger,
        borderRadius: 12,
        padding: '11px 13px',
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

export function NoticeBox({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: c.okBg,
        border: `1px solid ${c.okBg}`,
        color: c.okText,
        borderRadius: 12,
        padding: '12px 14px',
        fontSize: 14,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

/** Understated link, for "back to sign in" and similar. */
export function TextLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontSize: 14.5,
        fontFamily: sans,
        color: c.green,
        textDecoration: 'underline',
        textUnderlineOffset: 2,
      }}
    >
      {children}
    </button>
  );
}
